import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getCurrentUser, signInWithGoogle } from '../../services/supabase';
import { useNavigate } from 'react-router-dom';
import newLogo from '../../assets/wegogolf-logo-dark.png';
import { GoogleOAuthProvider } from '@react-oauth/google';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1rem;
  background-color: #f5f5f5;
`;

const LoginCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const LogoImage = styled.img`
  height: 80px;
  margin-bottom: 1rem;
  zoom: .7;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  margin-top: 1rem;
  text-align: center;
`;

const GoogleStyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  color: #757575;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 40px;
  contain: content;
  width: 100%;

  > svg {
    height: 18px;
    margin-right: 8px;
    float: left;
    min-width: 18px;
    width: 18px;
  }

  h5 {
    margin: 0;
    float: left;
    font-weight: 500;
    font-size: 1rem;
  }

  &:hover {
    background-color:rgb(224, 244, 255);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  img {
    margin-right: 0.5rem;
    height: 20px;
  }
`;

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Check for existing session on component mount
  useEffect(() => {
    const checkSession = async () => {
      setLoading(true);
      const { user, error } = await getCurrentUser();
      setLoading(false);
      
      if (user) {
        navigate('/dashboard');
      }
    };
    
    checkSession();
  }, [navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await signInWithGoogle();
      if (error) {
        throw error;
      }
      // Handle successful login, e.g., store tokens, redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <LoginContainer>
        <LoginCard>
          <Logo>
            <LogoImage src={newLogo} alt="We Go Golf" />
          </Logo>
          
          <GoogleStyledButton onClick={handleGoogleLogin} disabled={loading}>
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="LgbsSe-Bz112c"><g><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path><path fill="none" d="M0 0h48v48H0z"></path></g></svg>
            <h5>{loading ? 'Processing...' : 'Sign in with Google'}</h5>
          </GoogleStyledButton>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </LoginCard>
      </LoginContainer>
    </GoogleOAuthProvider>
  );
};

export default LoginPage; 